













































import { Component, Prop, Vue } from 'vue-property-decorator';

import CloseIcon from '@/components/icons/Close.vue';

import eventBus from '@/utils/eventBus';

import CoinIcon from '@/components/icons/WidgetCoin.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import CycleDays from '@/componentsV2/molecules/CycleDays/CycleDays.vue';
import BillingService from '@/api/BillingService';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import NavButtonItem from '@/componentsV2/molecules/NavButtonItem/NavButtonItem.vue';
import { features } from '@/components/organisms/ActivateFeatureWidgetModal/featuresHelper';

import Overview from './Steps/Overview.vue';
import Cycle from './Steps/Cycle.vue';
import Campaigns from './Steps/Campaigns.vue';
import OverviewMitingDate from './Steps/OverviewMitingDate.vue';
import DateUtils from '@/utils/DateUtils';
import flagKeys from '@/constants/flagKeys';
import segmentIo from '@/utils/segment.io';
import { getNeedsOnboardingCall } from '@/utils/onboardingCall';

export enum EBillingReactivationModalStepSlug {
  OVERVIEW = 'overview',
  OVERVIEW_MITING_DATE = 'overview_miting_date',
  CYCLE = 'cycle',
  CAMPAIGNS = 'campaigns'
}

@Component({
  components: {
    CloseIcon,
    CoinIcon,
    Typography,
    FilledButton,
    CycleDays,
    NavButtonItem,
    Overview,
    Cycle,
    Campaigns,
    OverviewMitingDate
  }
})
export default class CoinNetworkReactivationModal extends Vue {
  @Prop() subscription!: any;
  @Prop() origin?: string;
  @Prop() onSuccess?: () => void;
  @Prop() features?: Record<string, string>;

  loading = false;

  loadingFeatures = false;

  service = new BillingService();

  activeStepSlug: string = '';

  campaigns = features;

  get business() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get tPrefix() {
    return 'modals.billing.reactivation';
  }

  get hasSteps() {
    return this.steps.length > 1;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get billingModuleV3() {
    return this.flags.includes(flagKeys.billingModuleV3);
  }

  get delayedPeriod() {
    return (
      (this.billingModuleV3 && this.subscription.eligibleForDelayPeriod) ||
      getNeedsOnboardingCall()
    );
  }

  get steps(): any[] {
    const steps = [
      {
        slug: this.delayedPeriod
          ? EBillingReactivationModalStepSlug.OVERVIEW_MITING_DATE
          : EBillingReactivationModalStepSlug.OVERVIEW,
        label: 'Plan overview'
      }
    ];

    if (this.lessThanSevenDays && !this.delayedPeriod) {
      steps.push({
        slug: EBillingReactivationModalStepSlug.CYCLE,
        label: 'Your cycle'
      });
    }

    if (
      this.hasCampaigns &&
      this.business.subscriptionStatus !== EBillingStatusTypes.TO_BE_CANCELED
    ) {
      steps.push({
        slug: EBillingReactivationModalStepSlug.CAMPAIGNS,
        label: 'Your campaigns'
      });
    }

    return steps;
  }

  get tSecondaryButton() {
    let btn = 'back';

    if (this.activeStepSlug === this.steps[0].slug) {
      btn = this.steps.length > 1 ? 'cancel' : 'cancelReactivation';
    }

    return `${this.tPrefix}.secondaryButton.${btn}`;
  }

  get tPrimaryButton() {
    let btn = 'continue';

    if (this.steps.length > 1 && this.activeStepSlug === this.steps[0].slug)
      btn = 'getStarted';

    if (this.activeStepSlug === this.steps[this.steps.length - 1].slug)
      btn = 'confirm';

    return `${this.tPrefix}.primaryButton.${btn}`;
  }

  get componentByStepSlug(): {
    [k: string]: {
      component: string;
    };
  } {
    return {
      [EBillingReactivationModalStepSlug.OVERVIEW]: {
        component: 'Overview'
      },
      [EBillingReactivationModalStepSlug.OVERVIEW_MITING_DATE]: {
        component: 'OverviewMitingDate'
      },
      [EBillingReactivationModalStepSlug.CYCLE]: {
        component: 'Cycle'
      },
      [EBillingReactivationModalStepSlug.CAMPAIGNS]: {
        component: 'Campaigns'
      }
    };
  }

  get lessThanSevenDays() {
    const now = DateUtils.formattedDate({
      date: new Date(),
      applyTimezone: true
    });
    const Difference_In_Days = DateUtils.getDifferenceDays(now, this.endDate);

    return Difference_In_Days >= 1 && Difference_In_Days <= 7;
  }

  get hasCampaigns() {
    return !!this.campaigns.length;
  }

  get startDate() {
    return this.subscription.billingCycle.startAt;
  }

  get endDate() {
    return this.subscription.nextPaymentAt;
  }

  onSecondaryClick() {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug
    );

    if (this.activeStepSlug === this.steps[0].slug) {
      this.onClose();
    } else {
      segmentIo.track('Changed Step Billing Reactivation Flow', {
        'Actual Step': this.activeStepSlug,
        'Go To Step': this.steps[indexOfStep - 1].slug,
        Origin: this.origin,
        'Number Of Steps': this.steps.length
      });
      this.activeStepSlug = this.steps[indexOfStep - 1].slug;
    }
  }

  onPrimaryClick() {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug
    );
    if (indexOfStep === this.steps.length - 1) {
      segmentIo.track('Clicked Billing Reactivation Flow Finish', {
        Origin: this.origin
      });
      this.finish();
    } else {
      segmentIo.track('Changed Step Billing Reactivation Flow', {
        'Actual Step': this.activeStepSlug,
        'Go To Step': this.steps[indexOfStep + 1].slug,
        Origin: this.origin,
        'Number Of Steps': this.steps.length
      });
      this.activeStepSlug = this.steps[indexOfStep + 1].slug;
    }
  }

  async finish() {
    this.loading = true;
    try {
      let status = this.delayedPeriod
        ? EBillingStatusTypes.DELAY_PERIOD
        : EBillingStatusTypes.SUBSCRIBED;

      const { data } = await this.service.activateSubscription();

      if (data) {
        if (getNeedsOnboardingCall()) {
          status = EBillingStatusTypes.PENDING;
          this.$store.dispatch('business/SET_BILLING_SUBSCRIPTION', {
            ...this.subscription,
            status: EBillingStatusTypes.PENDING
          });
        }

        this.$store.dispatch('business/SET_BUSINESS_INFO', {
          ...this.business,
          subscriptionStatus: status
        });
        eventBus.$emit('reloadCoinNetworkOverview');
        eventBus.$emit('reloadCoinNetworkActivities', { clearList: true });

        this.onClose();
        if (this.onSuccess) this.onSuccess();
      }
    } finally {
      this.loading = false;
    }
  }

  isActiveStep(stepSlug: any) {
    return this.activeStepSlug === stepSlug;
  }

  onClose() {
    eventBus.$emit('closeModal');
  }

  percentageConcluded(step: any) {
    const indexOfStep = this.steps.findIndex(
      refStep => refStep.slug === step.slug
    );

    const currentIndex = this.steps.findIndex(
      refStep => refStep.slug === this.activeStepSlug.split(':')[0]
    );

    const isPrevLink = indexOfStep < currentIndex;

    return this.isActiveStep(step.slug) || isPrevLink ? 100 : 0;
  }

  async created() {
    this.activeStepSlug = this.steps[0].slug;
    let availableFeatures: any = this.features;

    if (!this.features) {
      try {
        this.loadingFeatures = true;
        const { data } = await this.service.getActivedFeatures();
        availableFeatures = data;
      } finally {
        this.loadingFeatures = false;
      }
    }

    const featuresAvailables = Object.keys(availableFeatures);

    this.campaigns = this.campaigns
      .filter((feature: any) => featuresAvailables.includes(feature.value))
      .map((feature: any) => {
        feature.description = feature.getDescription(
          availableFeatures[feature.value]
        );
        return feature;
      });

    segmentIo.modal('Billing Reactivation', {
      Origin: this.origin
    });
  }
}
